import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createLead, updateLead, getLead, getUsers, getClients } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatApiError, getDuplicateLead } from '../../lib/utils';
import { LEAD_STATUSES, SERVICE_INTERESTS, CLOSE_REASONS } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft } from '@phosphor-icons/react';

export default function LeadFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [duplicateLead, setDuplicateLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [sources, setSources] = useState([]);
  const [campaignNames, setCampaignNames] = useState([]);

  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone: '',
    secondary_phone: '',
    tertiary_phone: '',
    email: '',
    dob: '',
    member_id: '',
    source: '',
    campaign_name: '',
    referred_by_client_id: '',
    service_interest: [],
    status: 'New',
    close_reason: '',
    close_reason_other: '',
    assigned_to: '',
    notes: ''
  });

  const isReferral = formData.source === 'Referral';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, clientsRes, sourcesRes, campaignsRes] = await Promise.all([
          getUsers(),
          getClients(),
          api.get('/settings/lead_sources').catch(() => ({ data: { items: [] } })),
          api.get('/settings/campaign_names').catch(() => ({ data: { items: [] } }))
        ]);

        setUsers(usersRes.data);
        setClients(clientsRes.data);

        const activeSources = sourcesRes.data.items?.filter(i => i.is_active).map(i => i.value) || [];
        setSources(activeSources.length > 0 ? activeSources : ['Referral', 'Offline', 'Online', 'Instagram', 'LinkedIn', 'Email', 'Mobile', 'Facebook']);

        const activeCampaigns = campaignsRes.data.items?.filter(i => i.is_active).map(i => i.value) || [];
        setCampaignNames(activeCampaigns.length > 0 ? activeCampaigns : ['Mobile Stand', 'No Parking', 'Organic', 'Mouse Pad', 'Bags', 'Others']);

        if (isEdit) {
          const leadRes = await getLead(id);
          setFormData(prev => ({
            ...prev,
            ...leadRes.data,
            campaign_name: leadRes.data.campaign_name || leadRes.data.campaign_source || '',
            service_interest: leadRes.data.service_interest || []
          }));
        } else {
          setFormData(prev => ({ ...prev, assigned_to: user?.id || '' }));
        }
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit, user]); // eslint-disable-line react-hooks/exhaustive-deps -- refetches on id/isEdit/user changes only

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'source' && value === 'Referral') {
        updated.campaign_name = '';
      }
      if (field === 'source' && value !== 'Referral') {
        updated.referred_by_client_id = '';
      }
      return updated;
    });
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      service_interest: prev.service_interest.includes(interest)
        ? prev.service_interest.filter(s => s !== interest)
        : [...prev.service_interest, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.source) { toast.error('Please select a source'); return; }
    setDuplicateLead(null);
    setSaving(true);
    try {
      const payload = {
        ...formData,
        campaign_name: isReferral ? '' : formData.campaign_name,
        referred_by_client_id: isReferral ? formData.referred_by_client_id : null,
      };

      if (isEdit) {
        await updateLead(id, payload);
        toast.success('Lead updated successfully');
      } else {
        await createLead(payload);
        toast.success('Lead created successfully');
      }
      navigate('/leads');
    } catch (error) {
      const dup = getDuplicateLead(error);
      if (dup) {
        setDuplicateLead(dup);
        toast.error(formatApiError(error));
      } else {
        toast.error(formatApiError(error));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-[600px]" />
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="lead-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/leads')} className="p-2">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {isEdit ? 'Edit Lead' : 'New Lead'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        {duplicateLead && (
          <div className="bg-rose-50 border border-rose-200 rounded-sm p-4 flex items-start gap-3" data-testid="duplicate-lead-warning">
            <div className="flex-1 text-sm">
              <p className="font-semibold text-rose-800 mb-1">Duplicate lead detected</p>
              <p className="text-rose-700">
                A lead with this {duplicateLead.phone ? 'phone' : 'identifier'} already exists:&nbsp;
                <strong>{duplicateLead.full_name || duplicateLead.company_name || 'Unnamed'}</strong>
                {duplicateLead.phone && <span className="tabular-nums"> ({duplicateLead.phone})</span>}
                {duplicateLead.member_id && <span> · Member ID {duplicateLead.member_id}</span>}
              </p>
              <Button
                type="button"
                variant="link"
                className="px-0 text-rose-700 hover:text-rose-900"
                onClick={() => navigate(`/leads/${duplicateLead.id}`)}
                data-testid="view-existing-lead-link"
              >
                View existing lead →
              </Button>
            </div>
          </div>
        )}
        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Contact Person Name</Label>
                <Input value={formData.full_name} onChange={(e) => handleChange('full_name', e.target.value)} className="rounded-sm" data-testid="lead-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Company Name</Label>
                <Input value={formData.company_name} onChange={(e) => handleChange('company_name', e.target.value)} className="rounded-sm" data-testid="lead-company-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Phone <span className="text-red-500">*</span></Label>
                <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} required pattern="[6-9][0-9]{9}" className="rounded-sm" data-testid="lead-phone-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Secondary Phone</Label>
                <Input value={formData.secondary_phone} onChange={(e) => handleChange('secondary_phone', e.target.value)} className="rounded-sm" data-testid="lead-secondary-phone-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Tertiary Phone</Label>
                <Input value={formData.tertiary_phone} onChange={(e) => handleChange('tertiary_phone', e.target.value)} className="rounded-sm" data-testid="lead-tertiary-phone-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} className="rounded-sm" data-testid="lead-email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Date of Birth</Label>
                <Input type="date" value={formData.dob || ''} onChange={(e) => handleChange('dob', e.target.value)} className="rounded-sm" data-testid="lead-dob-input" />
              </div>
              {(formData.source || '').toLowerCase() === 'gymkhana' && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Member ID (Gymkhana)</Label>
                  <Input value={formData.member_id || ''} onChange={(e) => handleChange('member_id', e.target.value)} className="rounded-sm" data-testid="lead-member-id-input" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Source & Campaign</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Source <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  value={formData.source}
                  onValueChange={(v) => handleChange('source', v)}
                  options={sources}
                  placeholder="Select source"
                  data-testid="lead-source-select"
                />
              </div>
              {!isReferral && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Campaign Name <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    value={formData.campaign_name}
                    onValueChange={(v) => handleChange('campaign_name', v)}
                    options={campaignNames}
                    placeholder="Select campaign"
                    data-testid="lead-campaign-select"
                  />
                </div>
              )}
              {isReferral && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Referred By (Client)</Label>
                  <SearchableSelect
                    value={formData.referred_by_client_id}
                    onValueChange={(v) => handleChange('referred_by_client_id', v)}
                    options={clients.map(c => ({ value: c.id, label: c.full_name }))}
                    placeholder="Select referring client"
                    searchPlaceholder="Search clients..."
                    data-testid="lead-referrer-select"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Assigned To <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  value={formData.assigned_to}
                  onValueChange={(v) => handleChange('assigned_to', v)}
                  options={users.map(u => ({ value: u.id, label: u.name }))}
                  placeholder="Select user"
                  searchPlaceholder="Search users..."
                  data-testid="lead-assigned-select"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Service Interest</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SERVICE_INTERESTS.map(interest => (
                <label key={interest} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-zinc-50">
                  <Checkbox
                    checked={formData.service_interest.includes(interest)}
                    onCheckedChange={() => handleInterestToggle(interest)}
                    data-testid={`interest-${interest.replace(/\s+/g, '-')}`}
                  />
                  <span className="text-sm">{interest}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {isEdit && (
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100">
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Lead Status</Label>
                  <SearchableSelect
                    value={formData.status}
                    onValueChange={(v) => handleChange('status', v)}
                    options={LEAD_STATUSES}
                    placeholder="Select status"
                    data-testid="lead-status-select"
                  />
                </div>
                {formData.status === 'Closed' && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Close Reason</Label>
                      <SearchableSelect
                        value={formData.close_reason}
                        onValueChange={(v) => handleChange('close_reason', v)}
                        options={CLOSE_REASONS}
                        placeholder="Select reason"
                      />
                    </div>
                    {formData.close_reason === 'Other' && (
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">Other Reason</Label>
                        <Input
                          value={formData.close_reason_other}
                          onChange={(e) => handleChange('close_reason_other', e.target.value)}
                          className="rounded-sm"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any initial notes about this lead..."
              className="rounded-sm min-h-[100px]"
              data-testid="lead-notes-input"
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/leads')} className="rounded-sm">Cancel</Button>
          <Button
            type="submit" disabled={saving}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
            data-testid="lead-submit-button"
          >
            {saving ? 'Saving...' : (isEdit ? 'Update Lead' : 'Create Lead')}
          </Button>
        </div>
      </form>
    </div>
  );
}
